import { API } from '@/lib/api';
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ArrowLeft, Package, Clock, CheckCircle2, AlertCircle, User,
  MapPin, Phone, Mail, Calendar, DollarSign, ChevronRight, X, Loader2,
  Truck, Activity, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";



const STATUS_CONFIG = {
  pending: { color: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-400", label: "Pending" },
  accepted: { color: "bg-blue-100 text-blue-800 border-blue-200", dot: "bg-blue-400", label: "Accepted" },
  booked: { color: "bg-violet-100 text-violet-800 border-violet-200", dot: "bg-violet-400", label: "Booked" },
  completed: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-400", label: "Completed" },
};

const TRIP_STATUS_CONFIG = {
  en_route: { color: "bg-sky-100 text-sky-700 border-sky-200", label: "En Route", dot: "bg-sky-500 animate-pulse" },
  arriving: { color: "bg-indigo-100 text-indigo-700 border-indigo-200", label: "Arriving", dot: "bg-indigo-500 animate-pulse" },
  arrived: { color: "bg-teal-100 text-teal-700 border-teal-200", label: "Arrived", dot: "bg-teal-500" },
  completed: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Trip Done", dot: "bg-emerald-500" },
};

const TIMELINE_STEPS = ["pending", "accepted", "booked", "completed"];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const TripBadge = ({ tripStatus }) => {
  if (!tripStatus) return null;
  const cfg = TRIP_STATUS_CONFIG[tripStatus];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.color}`} data-testid={`trip-badge-${tripStatus}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-emerald-950">{value}</p>
      <p className="text-xs text-stone-500">{label}</p>
    </div>
  </div>
);

const BookingCard = ({ booking, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    whileTap={{ scale: 0.99 }}
    onClick={onClick}
    className="bg-white rounded-2xl border border-stone-100 p-4 cursor-pointer hover:border-emerald-200 hover:shadow-sm transition-all"
    data-testid={`booking-card-${booking.id}`}
  >
    <div className="flex items-center justify-between gap-3">
      {/* Left: Client + Service */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-emerald-950 truncate">{booking.customer_name}</h4>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="text-xs font-medium border-stone-200">{booking.service_label}</Badge>
          {booking.location && (
            <span className="text-xs text-stone-400 truncate flex items-center gap-1">
              <MapPin className="w-3 h-3" />{booking.location.substring(0, 30)}{booking.location.length > 30 ? "..." : ""}
            </span>
          )}
        </div>
      </div>

      {/* Middle: Professional + Date */}
      <div className="hidden sm:block text-right flex-shrink-0">
        <p className="text-sm text-stone-600">{booking.professional_name}</p>
        <p className="text-xs text-stone-400">
          {booking.details?.service_date || booking.details?.event_date || new Date(booking.created_at).toLocaleDateString()}
        </p>
      </div>

      {/* Right: Status + Trip */}
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <StatusBadge status={booking.effective_status} />
        {booking.trip_status && booking.effective_status === "accepted" && (
          <TripBadge tripStatus={booking.trip_status} />
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
    </div>
  </motion.div>
);

const BookingDetail = ({ booking, onClose }) => {
  if (!booking) return null;
  const currentStepIndex = TIMELINE_STEPS.indexOf(booking.effective_status);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="booking-detail-overlay"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
        data-testid="booking-detail-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-100 p-5 flex items-center justify-between rounded-t-3xl z-10">
          <div>
            <StatusBadge status={booking.effective_status} />
            <p className="text-xs text-stone-400 mt-1 font-mono">ID: {booking.id?.substring(0, 12)}...</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition" data-testid="close-detail-btn">
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Timeline */}
          <div data-testid="booking-timeline">
            <h5 className="text-sm font-semibold text-emerald-950 mb-3">Progress</h5>
            <div className="flex items-center justify-between relative">
              {/* Connector line */}
              <div className="absolute top-3 left-6 right-6 h-0.5 bg-stone-200" />
              <div className="absolute top-3 left-6 h-0.5 bg-emerald-500 transition-all" style={{ width: `${Math.max(0, (currentStepIndex / (TIMELINE_STEPS.length - 1)) * 100)}%`, maxWidth: "calc(100% - 48px)" }} />

              {TIMELINE_STEPS.map((step, i) => {
                const isActive = i <= currentStepIndex;
                const isCurrent = i === currentStepIndex;
                return (
                  <div key={step} className="flex flex-col items-center z-10 relative">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      isCurrent ? "bg-emerald-500 border-emerald-500 ring-4 ring-emerald-100" :
                      isActive ? "bg-emerald-500 border-emerald-500" :
                      "bg-white border-stone-300"
                    }`}>
                      {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <span className={`text-xs mt-1.5 capitalize ${isCurrent ? "font-semibold text-emerald-700" : isActive ? "text-emerald-600" : "text-stone-400"}`}>
                      {step}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Client Info */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="client-info">
            <h5 className="text-sm font-semibold text-emerald-950 mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-stone-500" /> Client
            </h5>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-stone-700">
                <User className="w-3.5 h-3.5 text-stone-400" /> {booking.customer_name}
              </div>
              {booking.customer_phone && (
                <div className="flex items-center gap-2 text-stone-700">
                  <Phone className="w-3.5 h-3.5 text-stone-400" /> {booking.customer_phone}
                </div>
              )}
              {booking.customer_email && (
                <div className="flex items-center gap-2 text-stone-700">
                  <Mail className="w-3.5 h-3.5 text-stone-400" /> {booking.customer_email}
                </div>
              )}
              {booking.location && (
                <div className="flex items-center gap-2 text-stone-700">
                  <MapPin className="w-3.5 h-3.5 text-stone-400" /> {booking.location}
                </div>
              )}
            </div>
          </div>

          {/* Professional Info */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="professional-info">
            <h5 className="text-sm font-semibold text-emerald-950 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-stone-500" /> Professional
            </h5>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-stone-700">
                <User className="w-3.5 h-3.5 text-stone-400" /> {booking.professional_name}
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={booking.professional_id ? "accepted" : "pending"} />
                <span className="text-xs text-stone-500">{booking.professional_id ? "Assigned" : "Not Assigned"}</span>
              </div>
            </div>
          </div>

          {/* Activity Status */}
          {booking.trip_status && (
            <div className={`rounded-2xl p-4 border ${
              booking.trip_status === "en_route" ? "bg-sky-50 border-sky-200" :
              booking.trip_status === "arriving" ? "bg-indigo-50 border-indigo-200" :
              booking.trip_status === "arrived" ? "bg-teal-50 border-teal-200" :
              booking.trip_status === "completed" ? "bg-emerald-50 border-emerald-200" :
              "bg-stone-50 border-stone-200"
            }`} data-testid="activity-status">
              <h5 className="text-sm font-semibold text-emerald-950 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-stone-500" /> Activity Status
              </h5>
              {/* Trip progress bar */}
              <div className="flex items-center justify-between mb-3">
                {["en_route", "arriving", "arrived", "completed"].map((step, i) => {
                  const tripSteps = ["en_route", "arriving", "arrived", "completed"];
                  const currentIdx = tripSteps.indexOf(booking.trip_status);
                  const isActive = i <= currentIdx;
                  const isCurrent = i === currentIdx;
                  const stepCfg = TRIP_STATUS_CONFIG[step];
                  return (
                    <div key={step} className="flex flex-col items-center flex-1 relative">
                      {i > 0 && (
                        <div className={`absolute top-3 right-1/2 w-full h-0.5 -z-0 ${isActive ? "bg-emerald-400" : "bg-stone-200"}`} />
                      )}
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center z-10 ${
                        isCurrent ? "bg-emerald-500 border-emerald-500 ring-4 ring-emerald-100" :
                        isActive ? "bg-emerald-500 border-emerald-500" : "bg-white border-stone-300"
                      }`}>
                        {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <span className={`text-[10px] mt-1 text-center ${isCurrent ? "font-bold text-emerald-700" : isActive ? "text-emerald-600" : "text-stone-400"}`}>
                        {stepCfg?.label || step}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <TripBadge tripStatus={booking.trip_status} />
                <span className="text-xs text-stone-500">
                  {booking.trip_status === "en_route" ? "Professional is heading to customer" :
                   booking.trip_status === "arriving" ? "Professional is almost there" :
                   booking.trip_status === "arrived" ? "Professional has arrived at location" :
                   booking.trip_status === "completed" ? "Service has been completed" :
                   ""}
                </span>
              </div>
            </div>
          )}

          {/* Service Details */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="service-details">
            <h5 className="text-sm font-semibold text-emerald-950 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4 text-stone-500" /> Service Details
            </h5>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Type</span>
                <Badge variant="outline">{booking.service_label}</Badge>
              </div>
              {Object.entries(booking.details || {}).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-stone-500 capitalize">{key.replace(/_/g, " ")}</span>
                  <span className="text-stone-700 font-medium text-right max-w-[60%] truncate">
                    {typeof val === "number" && key.includes("price") ? `$${val}` : String(val)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Created</span>
                <span className="text-stone-700">{new Date(booking.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const AdminBookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, completed: 0, completed_today: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${API}/admin/all-bookings`, {
        params: { search, status: statusFilter },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setBookings(res.data.bookings || []);
      setStats(res.data.stats || {});
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => fetchBookings(), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const tabs = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "accepted", label: "Accepted" },
    { key: "booked", label: "Booked" },
    { key: "completed", label: "Completed" },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-bookings-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-emerald-950">Bookings</h1>
            <Badge className="bg-emerald-100 text-emerald-800 ml-auto">{stats.total} total</Badge>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client, professional, service..."
              className="pl-10 rounded-xl border-stone-200"
              data-testid="search-input"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="filter-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  statusFilter === tab.key
                    ? "bg-emerald-950 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
                data-testid={`filter-${tab.key}`}
              >
                {tab.label}
                {tab.key !== "all" && (
                  <span className="ml-1.5 text-xs opacity-70">
                    {tab.key === "pending" ? stats.pending : tab.key === "accepted" ? stats.accepted : tab.key === "completed" ? stats.completed : ""}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="stats-section">
          <StatCard label="Total Bookings" value={stats.total} icon={Package} color="bg-emerald-600" />
          <StatCard label="Active" value={stats.accepted + (stats.booked || 0)} icon={Activity} color="bg-blue-500" />
          <StatCard label="Completed Today" value={stats.completed_today} icon={CheckCircle2} color="bg-green-500" />
          <StatCard label="Pending" value={stats.pending} icon={Clock} color="bg-amber-500" />
        </div>

        {/* Booking List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2" data-testid="loading-state">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
            <span className="text-stone-500">Loading bookings...</span>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Filter className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 font-medium">No bookings found</p>
            <p className="text-sm text-stone-400 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {bookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onClick={() => setSelectedBooking(booking)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedBooking && (
          <BookingDetail
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBookingsPage;
